import dayjs from "dayjs";
import { env } from '@stock.front/env'
type DateString = `${number}-${number}-${number}`;

const holidays = ["2025-05-31", "2025-06-01", "2025-06-02", "2025-10-01", "2025-10-02", "2025-10-03", "2025-10-04", "2025-10-05", "2025-10-06", "2025-10-07", "2025-10-08"]

//计算交易到期时间
export function calculateTradeDate(startDate: Date, n: number): Date {
    const currentDate = new Date(startDate);
    let count = 0;
    const holidaySet = new Set(holidays);

    while (count < n) {
        // 每次循环移动到下一天
        currentDate.setDate(currentDate.getDate() + 1);

        // 检查是否为交易日（周一至周五）
        const dayOfWeek = currentDate.getDay();
        const dateString = currentDate.toISOString().split('T')[0] as DateString;

        if (dayOfWeek !== 0 && dayOfWeek !== 6 && !holidaySet.has(dateString)) { // 0=周日, 6=周六
            count++;
        }
    }

    return currentDate;
}

/**
 * 获取A股上一个交易时间信息
 * @returns 返回状态信息，格式："正常" 或 "已收盘 YYYY-MM-DD HH:mm"
 */
export function getLastTradeTime(): string {
    // 转换为北京时间（UTC+8）
    const beijingTime = getBeijingTime(dayjs().toDate());
    const { year, month, day, hours, minutes } = beijingTime;
    const currentTime = hours * 100 + minutes;
    const holidaySet = new Set(holidays);

    const dateString = new Date().toISOString().split('T')[0] as DateString;

    const weekday = getBeijingWeekday(year, month, day);
    const isTradingDay = weekday >= 1 && weekday <= 5 && !holidaySet.has(dateString) // 周一至周五视为交易日

    if (isTradingDay) {
        if ((currentTime >= 930 && currentTime < 1130) ||  // 上午交易时段
            (currentTime >= 1300 && currentTime < 1500)) { // 下午交易时段
            return "正常";
        } else {
            let lastCloseTime: string;
            if (currentTime < 930) {
                const prevDay = findPreviousTradingDay(year, month, day);
                lastCloseTime = formatLastTime(prevDay.year, prevDay.month, prevDay.day, 15, 0);
            } else if (currentTime < 1300) { // 午间休市
                lastCloseTime = formatLastTime(year, month, day, 11, 30);
            } else { // 收盘后
                lastCloseTime = formatLastTime(year, month, day, 15, 0);
            }
            return `已收盘 ${lastCloseTime}`;
        }
    } else { // 非交易日（周末/节假日）
        const prevDay = findPreviousTradingDay(year, month, day);
        return `已收盘 ${formatLastTime(prevDay.year, prevDay.month, prevDay.day, 15, 0)}`;
    }
}

// 辅助函数：北京时间转换
function getBeijingTime(date: Date) {
    const utcHours = date.getUTCHours();
    const adjustDate = new Date(date.getTime() + 8 * 3600 * 1000); // UTC+8偏移
    return {
        year: adjustDate.getUTCFullYear(),
        month: adjustDate.getUTCMonth(),
        day: adjustDate.getUTCDate(),
        hours: adjustDate.getUTCHours(),
        minutes: adjustDate.getUTCMinutes()
    };
}

// 辅助函数：获取北京时间的星期几
function getBeijingWeekday(year: number, month: number, day: number): number {
    const date = new Date(Date.UTC(year, month, day, -8)); // 北京时间的0点对应UTC前一日16点
    return (date.getUTCDay() + 1) % 7; // 转换为0=周日,1=周一...6=周六
}

// 辅助函数：查找前一个交易日
function findPreviousTradingDay(year: number, month: number, day: number) {
    let date = new Date(Date.UTC(year, month, day, -8)); // 转换为UTC时间
    for (let i = 0; i < 7; i++) {
        date.setUTCDate(date.getUTCDate() - 1);
        const weekday = getBeijingWeekday(
            date.getUTCFullYear(),
            date.getUTCMonth(),
            date.getUTCDate()
        );
        if (weekday >= 1 && weekday <= 5) {
            return {
                year: date.getUTCFullYear(),
                month: date.getUTCMonth(),
                day: date.getUTCDate()
            };
        }
    }
    throw new Error("找不到有效交易日");
}

// 辅助函数：格式化时间
function formatLastTime(year: number, month: number, day: number, hours: number, minutes: number) {
    return `${(month + 1).toString().padStart(2, '0')}-` +
        `${day.toString().padStart(2, '0')} ` +
        `${hours.toString().padStart(2, '0')}:` +
        `${minutes.toString().padStart(2, '0')}`;
}

//是否交易时间
export function isStockMarketTrading(): boolean {
    const now = new Date();
    // 转换为北京时间（UTC+8）
    const beijingTime = new Date(now.getTime() + (8 * 60 * 60 * 1000));

    // 获取北京时间的星期、小时和分钟
    const dayOfWeek = beijingTime.getUTCDay(); // 0 = 周日, 1 = 周一 ... 6 = 周六
    const hours = beijingTime.getUTCHours();
    const minutes = beijingTime.getUTCMinutes();

    const holidaySet = new Set(holidays);

    const dateString = new Date().toISOString().split('T')[0] as DateString;

    // 检查是否为工作日或节假日
    if (dayOfWeek === 0 || dayOfWeek === 6 || holidaySet.has(dateString) ) return false;

    // 转换为分钟总数便于比较
    const totalMinutes = hours * 60 + minutes;

    // 交易时段检查
    const isMorningSession = totalMinutes >= 570 && totalMinutes <= 690; // 09:30-11:30
    const isAfternoonSession = totalMinutes >= 780 && totalMinutes <= 900; // 13:00-15:00

    return isMorningSession || isAfternoonSession;
}

export function isWithinExpiredTime(): boolean {
    const now = new Date();
    const currentHours = now.getHours();    // 获取当前小时（本地时区）
    const currentMinutes = now.getMinutes();// 获取当前分钟

    // 将时间转换为分钟总数进行比较
    const totalMinutes = currentHours * 60 + currentMinutes;
    const start = 14 * 60 + 55; // 14:55 的分钟总数
    const end = 15 * 60;        // 15:00 的分钟总数

    return totalMinutes >= start && totalMinutes <= end;
}

/**
 * 验证当前时间是否在交易配置的时间范围内
 * 当时间不符合要求时抛出错误
 */
export function validateTradingTime(canCreateStart: Date,canCreateEnd: Date,canTradeStart: Date,canTradeEnd: Date, ) {

    if(process.env.NODE_ENV != 'production') {
        return {code:0, message: '可交易'}
    }

    const now = new Date();
    // 验证可创建时间段
    if (!isWithinTimeRange(now, canCreateStart, canCreateEnd)) {
        return {code:-1, message: (`当前时间不在允许的创建时间段内（${formatTime(canCreateStart)} - ${formatTime(canCreateEnd)}）`)};
    }

    // 验证可交易时间段
    if (!isWithinTimeRange(now, canTradeStart, canTradeEnd)) {
        return {code:-2, message: (`当前时间不在允许的交易时间段内（${formatTime(canTradeStart)} - ${formatTime(canTradeEnd)}）`)};
    }

    return {code:0, message: '可交易'}
}

/**
 * 判断时间是否在当日的时间范围内（支持跨天时间段）
 */
function isWithinTimeRange(current: Date, start: Date, end: Date): boolean {
    const currentMins = getMinutes(current);
    const startMins = getMinutes(start);
    const endMins = getMinutes(end);

    // 处理跨天时间段（例如 22:00 - 02:00）
    return startMins <= endMins
        ? currentMins >= startMins && currentMins <= endMins
        : currentMins >= startMins || currentMins <= endMins;
}

/**
 * 将时间转换为分钟数（忽略日期部分）
 */
function getMinutes(date: Date): number {
    return dayjs(date).hour() * 60 + dayjs(date).minute();
}

/**
 * 格式化时间显示（HH:mm）
 */
function formatTime(date: Date): string {
    return date.toTimeString().slice(0, 5);
}